import {
  getListing,
  getBuildingBySlug,
  getListingSearchPageLevelArguments,
  getLandlord,
} from '@store/selectors';
import { StoreState } from '@root/types';

import { page, track, identify } from '@root/tracking/analytics';

const eventTypeToAnalyticsFunction = {
  page,
  track,
  identify,
};

const isNotAnAnalyticsAction = action => !action.payload?.meta?.analytics;

const getListingPageLevelArguments = (state: StoreState) => {
  const listing = getListing(state, state.page.pageArguments.id);
  if (!listing) return {};

  const buildingEmployerId = listing.building.employerId;
  const listingAgentsEmails = listing.listingAgents?.map(({ email }) => email);

  return {
    listingPreview: state.page.pageArguments.isPreview,
    buildingEmployerId,
    listingAgentsEmails,
    ...listing.analyticsInformation,
  };
};

const getListingSharePageLevelArguments = (state: StoreState) => {
  const listing = state.listings.listingShare;
  return {
    listingPreview: false,
    ...listing!.analyticsInformation,
  };
};

const getLandlordPageLevelArguments = (state: StoreState) => {
  const landlord = getLandlord(state, state.page.pageArguments.slug);
  return landlord?.analyticsInformation || {};
};

const getBuildingPageLevelArguments = (state: StoreState) => {
  const building = getBuildingBySlug(state, state.page.pageArguments.slug);

  return {
    buildingPreview: state.page.pageArguments.isPreview,
    ...building?.analyticsInformation,
  };
};

const getTourbookPageLevelArguments = state => {
  const tourbook = state.tourbooks.byId[state.page.pageArguments.id];

  return {
    tourbookName: tourbook.name,
    tourbookId: tourbook.id,
    tourbookOwnerId: tourbook.owner.id,
    isShare: state.page.pageArguments.isShare,
    listingCount: tourbook.listings ? tourbook.listings.length : 0,
  };
};

const getPageLevelArguments = (state, skipPageInfo) => {
  if (skipPageInfo) return {};

  switch (state.page.type) {
    case 'LISTING': {
      return getListingPageLevelArguments(state);
    }
    case 'LISTING_SHARE': {
      return getListingSharePageLevelArguments(state);
    }
    case 'LISTING_SEARCH': {
      return getListingSearchPageLevelArguments(state);
    }
    case 'BUILDING': {
      return getBuildingPageLevelArguments(state);
    }
    case 'LANDLORD': {
      return getLandlordPageLevelArguments(state);
    }
    case 'VIDEO_EMBED': {
      return state.page.pageArguments;
    }
    case 'TOURBOOK': {
      return getTourbookPageLevelArguments(state);
    }
    default:
      return {};
  }
};

const analyticsMiddleware = store => {
  // Use event queuing to ensure analytics events are sent after the user state is
  // determined (e.g. the user is known or will be anonymous for the session).
  // This queuing solution was added to address this ticket:
  // https://viewthespace.atlassian.net/browse/TRV-1730
  let queue: { analyticsFn: (event, args) => void; event: any; args: any }[] = [];
  return next => action => {
    next(action);

    if (isNotAnAnalyticsAction(action)) return;

    if (action.payload.meta.analytics.forIdentification) {
      const { identifier, user } = action.payload.meta.analytics;

      if (user) {
        identify(identifier, user);
      }

      queue.forEach(({ analyticsFn, event, args }) => analyticsFn(event, args));
      queue = [];
      return;
    }

    const { eventType, event, skipPageInfo, ...analyticsArguments } = action.payload.meta.analytics;
    const analyticsFn = eventTypeToAnalyticsFunction[eventType];

    const pageLevelArguments = getPageLevelArguments(store.getState(), skipPageInfo);

    if (!store.getState().loadingStates?.currentUser) {
      queue.push({ analyticsFn, event, args: { ...pageLevelArguments, ...analyticsArguments } });
    } else {
      analyticsFn(event, { ...pageLevelArguments, ...analyticsArguments });
    }
  };
};

export default analyticsMiddleware;
